import React, { useEffect, useState } from 'react';
import {
	Box,
	Container,
	Header,
	SpaceBetween,
	Badge,
	ExpandableSection,
	Link,
	Flashbar,
	FlashbarProps,
	Spinner,
	Alert,
	Button,
	Modal,
} from '@amzn/awsui-components-console';
import { getTypeBadge, FlashbarItem } from 'src/components/WhatsNew/FeatureType';
import {
	ReleaseNote,
} from 'src/components/WhatsNew/WhatsNew';
import { useReleaseNotesStore } from 'src/stores/releaseNotesStore';
import { fetchFlashbarItems, deleteFlashbarItem } from 'src/services/flashbarService';
import AddContentModal from 'src/components/WhatsNew/AddContentModal/AddContentModal';
import { useAuthContext } from 'src/providers/AuthContext';
import {
	getBindleLockForNodeType,
	getBindleLockAuthorizationRequest,
} from 'src/authentication/bindle-utils';
import { callSNOWDataService } from 'src/utils/snowDataServiceWrapper';
import { logger } from 'src/utils/logger';
import { v4 as uuidv4 } from 'uuid';


interface WhatsNewPageProps {
	openTools?: () => void;
	setTools?: (config: {
		title: React.ReactNode;
		content: React.ReactNode;
		links: Array<{ href: string; text: string }>;
	}) => void;
	hideTools?: () => void;
}

const WhatsNewPage: React.FC<WhatsNewPageProps> = ({
													   openTools,
													   setTools,
													   hideTools,
												   }) => {
	const [flashbarItems, setFlashbarItems] = useState<
		FlashbarProps.MessageDefinition[]
	>([]);
	const [flashbarLoading, setFlashbarLoading] = useState(true);
	const { releaseNotes, isLoading, error, fetchIfNeeded } = useReleaseNotesStore();
	const [showAddModal, setShowAddModal] = useState(false);
	const [canAddRelease, setCanAddRelease] = useState<boolean | null>(null);
	const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
	const [itemToDelete, setItemToDelete] = useState<string | null>(null);
	const [editingRelease, setEditingRelease] = useState<ReleaseNote | null>(null);
	const { userName } = useAuthContext();

	const mapFlashbarItems = (items: FlashbarItem[]): FlashbarProps.MessageDefinition[] =>
		items.map((item) => ({
			type: item.type as FlashbarProps.Type,
			header: item.header,
			content: item.content,
			dismissible: item.dismissible ?? true,
			id: item.id,
		}));

	const refreshFlashbarItems = async () => {
		try {
			const flashbarData = await fetchFlashbarItems();
			const pageItems = flashbarData.filter(item => !item.isGlobal);
			setFlashbarItems(mapFlashbarItems(pageItems));
		} finally {
			setFlashbarLoading(false);
		}
	};

	useEffect(() => {
		const checkAuthorization = async () => {
			if (!userName) {
				setCanAddRelease(false);
				return;
			}
			try {
				const lockId = getBindleLockForNodeType('snow_team_internal');
				const response = await callSNOWDataService({
					path: '/authorize',
					body: {
						user: userName,
						resource: getBindleLockAuthorizationRequest([lockId]),
						requestId: uuidv4(),
					},
					method: 'POST',
				});
				logger.info(`Authorization response [ response ] ::: [ ${JSON.stringify(response)} ]`);
				const isAuthorized = !!(response?.authorizedResource?.ids?.length);
				logger.info(`Is authorized [ isAuthorized ] ::: [ ${isAuthorized} ]`);
				setCanAddRelease(isAuthorized);
			} catch (err) {
				logger.info(`Authorization error [ error ] ::: [ ${JSON.stringify(err)} ]`);
				setCanAddRelease(false);
			}
		};

		fetchIfNeeded(true);

		if (setTools) {
			setTools({
				title: <h2>About This Page</h2>,
				content: (
					<SpaceBetween size="m">
						<Box variant="p">
							This page lists impactful changes from the last few weeks,
							including new features, improvements, and bug fixes.
						</Box>
						<Box variant="p">Changes are shown newest first.</Box>
						<Box variant="p" color="text-body-secondary">
							<em>Note: Visit history is stored locally in your browser.</em>
						</Box>
					</SpaceBetween>
				),
				links: [
					{
						href: 'https://w.amazon.com/bin/view/SNOWHome',
						text: 'About SNOW',
					},
					{ href: '#/contact', text: 'Contact Us' },
				],
			});
		}

		checkAuthorization();
		refreshFlashbarItems();

		return () => {
			if (hideTools) {
				hideTools();
			}
		};
	}, [setTools, hideTools, userName, fetchIfNeeded]);

	const handleAddRelease = (releaseData: ReleaseNote) => {
		const updated = [...releaseNotes.filter((r) => r.version !== releaseData.version), releaseData]
			.sort((a, b) => b.version.localeCompare(a.version));
		useReleaseNotesStore.setState({ releaseNotes: updated });
		setShowAddModal(false);
	};

	const handleFlashbarDismiss = (itemId: string) => {
		setFlashbarItems((prevItems) =>
			prevItems.filter((item) => item.id !== itemId)
		);
	};

	const handleFlashbarDelete = async () => {
		if (!itemToDelete) return;
		try {
			await deleteFlashbarItem(itemToDelete);
			setFlashbarItems((prevItems) =>
				prevItems.filter((item) => item.id !== itemToDelete)
			);
			setDeleteConfirmVisible(false);
			setItemToDelete(null);
		} catch (error) {
			logger.error(`Failed to delete flashbar item [ error ] ::: [ ${JSON.stringify(error)} ]`);
			setDeleteConfirmVisible(false);
			setItemToDelete(null);
		}
	};

	const handleInfoClick = () => {
		if (openTools) {
			openTools();
		}
	};

	const renderReleaseCard = (
		release: ReleaseNote,
		isLatest: boolean = false
	) => (
		<Container
			key={release.version}
			header={
				<Header
					variant="h3"
					actions={
						<SpaceBetween direction="horizontal" size="xs">
							{isLatest && <Badge color="blue">Latest</Badge>}
							{canAddRelease && (
								<Button
									iconName="edit"
									variant="icon"
									ariaLabel={`Edit release ${release.version}`}
									onClick={() => {
										setEditingRelease(release);
										setShowAddModal(true);
									}}
								/>
							)}
						</SpaceBetween>
					}
				>
					{release.date}
				</Header>
			}
		>
			<SpaceBetween size="m">
				<SpaceBetween size="xs">
					<Box variant="strong">Highlights</Box>
					<ul>
						{release.highlights.map((highlight, i) => (
							<li key={i}>
								<Box variant="p">{highlight}</Box>
							</li>
						))}
					</ul>
				</SpaceBetween>

				<ExpandableSection
					headerText={`View all ${release.features.length} updates`}
					defaultExpanded={isLatest}
				>
					<SpaceBetween size="s">
						{release.features.map((feature, i) => (
							<SpaceBetween key={i} size="xxs">
								<SpaceBetween direction="horizontal" size="xs">
									{getTypeBadge(feature.type)}
									<Box variant="strong">{feature.title}</Box>
								</SpaceBetween>
								<Box variant="p" color="text-body-secondary">
									{feature.description}
								</Box>
							</SpaceBetween>
						))}
					</SpaceBetween>
				</ExpandableSection>

				{release.learnMoreLink && (
					<Link href={release.learnMoreLink} external>
						Learn more about this release
					</Link>
				)}
			</SpaceBetween>
		</Container>
	);

	if (error) {
		return (
			<Alert type="error" header="Failed to load release notes">
				{error}
			</Alert>
		);
	}

	return (
		<SpaceBetween size="l">
			<Header
				variant="h1"
				description="Stay up to date with the latest features and improvements in SNOW"
				actions={
					canAddRelease === true && (
						<Button iconName="add-plus" onClick={() => setShowAddModal(true)} />
					)
				}
				info={
					openTools && (
						<Link variant="info" onFollow={handleInfoClick}>
							Info
						</Link>
					)
				}
			>
				What&apos;s New
			</Header>

			<AddContentModal
				visible={showAddModal}
				onDismiss={() => {
					setShowAddModal(false);
					setEditingRelease(null);
				}}
				onSubmitRelease={(releaseData) => {
					handleAddRelease(releaseData);
					setEditingRelease(null);
				}}
				onSubmitFlashbar={async () => {
					setShowAddModal(false);
					setEditingRelease(null);
					await refreshFlashbarItems();
				}}
				editingRelease={editingRelease}
			/>

			<Modal
				visible={deleteConfirmVisible}
				onDismiss={() => {
					setDeleteConfirmVisible(false);
					setItemToDelete(null);
				}}
				header="Permanently Delete Flashbar"
				footer={
					<Box float="right">
						<SpaceBetween direction="horizontal" size="xs">
							<Button variant="link" onClick={() => {
								setDeleteConfirmVisible(false);
								setItemToDelete(null);
							}}>Cancel</Button>
							<Button variant="primary" onClick={handleFlashbarDelete}>Delete</Button>
						</SpaceBetween>
					</Box>
				}
			>
				Are you sure you want to permanently delete this flashbar? This action cannot be undone.
			</Modal>

			{flashbarItems.length > 0 && (
				<Flashbar
					items={flashbarItems.map((item) => ({
						...item,
						onDismiss:
							item.dismissible && item.id
								? () => handleFlashbarDismiss(item.id!)
								: undefined,
						action: canAddRelease && item.id ? (
							<Button iconName="remove" variant="icon" onClick={() => {
								setItemToDelete(item.id!);
								setDeleteConfirmVisible(true);
							}} />
						) : undefined,
					}))}
				/>
			)}

			<SpaceBetween size="m">
				{releaseNotes.length === 0 && isLoading ? (
					<Box textAlign="center" padding="xxl">
						<Spinner size="large" />
					</Box>
				) : (
					<>
						{releaseNotes.map((release, index) =>
							renderReleaseCard(release, index === 0)
						)}
						{isLoading && (
							<Box textAlign="center" padding="s">
								<Spinner /> Loading more releases...
							</Box>
						)}
					</>
				)}
			</SpaceBetween>
		</SpaceBetween>
	);
};

export default WhatsNewPage;
